import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import FileIcon from "@entrilia/shared/components/FileIcon";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import { formatFileSize, getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { ChangeRequestAttachment } from "../../../../api/types/workflowTypes";
import DocumentPreview from "../../../common/document-preview/DocumentPreview";
import PanelSection from "../../../common/layout/PanelSection";
import PanelSectionHeader from "../../../common/layout/PanelSectionHeader";

interface Props {
  workflowId: string;
  attachments: ChangeRequestAttachment[];
  collapsible?: boolean;
}

const ChangeRequestAttachmentsSection = ({ workflowId, attachments, collapsible }: Props) => {
  const [expanded, toggleExpanded] = useToggleState(true);
  const [index, setIndex] = useState(0);

  const current = defined(attachments[index]);

  const getFileDownloadInfo = useCallback(
    () => adminApi.getInvestorChangeRequestAttachmentDownloadInfo(workflowId, current.dataCatalogId),
    [workflowId, current.dataCatalogId]
  );

  return (
    <PanelSection flush sx={{ height: expanded ? "100%" : "auto", minHeight: expanded ? "50%" : undefined }}>
      <PanelSectionHeader
        title="Attachments"
        expanded={expanded}
        onToggleExpanded={collapsible ? toggleExpanded : undefined}
        flush
        centerContent={
          attachments.length > 1 && (
            <>
              <IconButton disabled={index === 0} onClick={() => setIndex(index - 1)}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="subtitle2" noWrap>
                {index + 1} of {attachments.length}
              </Typography>
              <IconButton disabled={index === attachments.length - 1} onClick={() => setIndex(index + 1)}>
                <ChevronRightIcon />
              </IconButton>
            </>
          )
        }
      />

      {expanded && (
        <>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", px: 2.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0, flex: 1 }}>
              <FileIcon fileExtension={getFileExtension(current.name)} />
              <Typography noWrap sx={{ minWidth: 0 }}>
                {current.name}
              </Typography>
            </Stack>
            <Typography noWrap sx={{ flexShrink: 0 }}>
              {formatFileSize(current.size)}
            </Typography>
          </Stack>

          <Divider flexItem />

          <DocumentPreview getFileDownloadInfoCallback={getFileDownloadInfo} />
        </>
      )}
    </PanelSection>
  );
};

export default ChangeRequestAttachmentsSection;
